import { AlertCircle, FileText, IdCard, Info, Loader2, Upload } from "lucide-react";
import { Thumbnail } from "@components/file-preview";
import {
  isDocumentFile,
  isImageFile,
  openFilePreview,
  openFilePreviewByPath,
  generateFilePreviewUrl,
  buildStreamFileUrl,
  ensureFileToken,
} from "@/lib/utils/filePreview";
import { ACCEPTED_INCOME_AND_ID_DOCUMENT_ACCEPT } from "@/lib/utils/acceptedDocumentFile";
import { normalizeGovIdAttachments } from "@/features/applicant/utils/applicationDataHelpers";
import type { ApplicationFileAttachment } from "@/types/application";
import type { useGovernmentIdUpload } from "@/features/applicant/hooks/useGovernmentIdUpload";

type GovernmentIdUploadState = ReturnType<typeof useGovernmentIdUpload>;

const GOV_ID_FILE_ACCEPT = ACCEPTED_INCOME_AND_ID_DOCUMENT_ACCEPT;

export interface GovernmentIdUploadSectionProps {
  gov: GovernmentIdUploadState;
  isReadOnly?: boolean;
  testIdPrefix?: string;
  embedded?: boolean;
}

export function GovernmentIdUploadSection({
  gov,
  isReadOnly = false,
  testIdPrefix = "gov-id",
  embedded = false,
}: GovernmentIdUploadSectionProps) {
  const {
    applicationData,
    governmentIdType,
    chooseGovernmentIdType,
    setDriversLicenseFront,
    setDriversLicenseBack,
    setPassportImage,
    driversLicenseFront,
    driversLicenseBack,
    passportImage,
    driversLicenseFrontUrl,
    driversLicenseBackUrl,
    passportImageUrl,
    uploadingFront,
    uploadingBack,
    uploadingPassport,
    fieldErrors,
    setFieldErrors,
    validateForm,
    driversLicenseFrontRef,
    driversLicenseBackRef,
    passportRef,
    handleFileUpload,
    saveMessage,
  } = gov;

  const storedFrontAttachments = normalizeGovIdAttachments(
    applicationData?.goverment_id_attachment,
  );
  const savedIdType = String(applicationData?.goverment_id_type || "").toLowerCase();
  const savedIsPassport = savedIdType.includes("passport");
  const savedIsDriversLicense =
    savedIdType.includes("driver") || savedIdType.includes("license");
  const hasPassportFile =
    Boolean(passportImage) ||
    (!savedIsDriversLicense && storedFrontAttachments.length > 0);

  const handleFilePreview = async (
    file: { filepath?: string; name: string; attachment_id?: number },
    fileType: "ID_PROOF" | "ID_PROOF_BACK" = "ID_PROOF",
  ) => {
    if (!file.filepath || !applicationData?.applicant_id) return;
    const applicantId = applicationData.applicant_id;

    if (file.attachment_id) {
      await ensureFileToken();
      const governmentIdFront = applicationData.government_id || [];
      const governmentIdBack = applicationData.government_id_back || [];
      const allGovernmentIdFiles = [
        ...(Array.isArray(governmentIdFront) ? governmentIdFront : []),
        ...(Array.isArray(governmentIdBack) ? governmentIdBack : []),
      ];
      const allFilesWithIds = allGovernmentIdFiles.filter(
        (f): f is ApplicationFileAttachment & { attachment_id: number } =>
          f.attachment_id != null,
      );
      const filesList = allFilesWithIds.map((f) => {
        const fileTypeForUrl = governmentIdFront.includes(f)
          ? "ID_PROOF"
          : "ID_PROOF_BACK";
        return generateFilePreviewUrl(
          f.attachment_id,
          fileTypeForUrl,
          applicantId,
          "APPLICATION",
        );
      });
      await openFilePreview(
        file.attachment_id,
        fileType,
        file.name || file.filepath,
        applicantId,
        "APPLICATION",
        filesList.length > 1 ? filesList : undefined,
      );
      return;
    }

    let filePath = file.filepath.trim();
    if (filePath.includes("/stream-file?path=")) {
      try {
        const url = new URL(filePath);
        const pathParam = url.searchParams.get("path");
        if (pathParam) filePath = decodeURIComponent(pathParam);
      } catch {
        const match = filePath.match(/path=([^&]+)/);
        if (match) filePath = decodeURIComponent(match[1]);
      }
    } else if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
      try {
        const url = new URL(filePath);
        filePath = url.pathname.replace(/^\//, "");
      } catch {
        return;
      }
    }
    filePath = filePath.replace(/^\//, "");
    const previewUrl = buildStreamFileUrl(filePath);
    const governmentIdFront = applicationData.government_id || [];
    const governmentIdBack = applicationData.government_id_back || [];
    const allGovernmentIdFiles = [
      ...(Array.isArray(governmentIdFront) ? governmentIdFront : []),
      ...(Array.isArray(governmentIdBack) ? governmentIdBack : []),
    ];
    const allFilesWithoutIds = allGovernmentIdFiles.filter(
      (f): f is ApplicationFileAttachment & { filepath: string } =>
        !f.attachment_id && !!f.filepath,
    );
    const filesList = allFilesWithoutIds
      .map((f) => {
        let fPath = f.filepath.trim();
        if (fPath.includes("/stream-file?path=")) {
          try {
            const url = new URL(fPath);
            const pathParam = url.searchParams.get("path");
            if (pathParam) fPath = decodeURIComponent(pathParam);
          } catch {
            const match = fPath.match(/path=([^&]+)/);
            if (match) fPath = decodeURIComponent(match[1]);
          }
        } else if (fPath.startsWith("http://") || fPath.startsWith("https://")) {
          try {
            const url = new URL(fPath);
            fPath = url.pathname.replace(/^\//, "");
          } catch {
            return null;
          }
        }
        fPath = fPath.replace(/^\//, "");
        return buildStreamFileUrl(fPath);
      })
      .filter((url): url is string => url !== null);
    openFilePreviewByPath(
      previewUrl,
      file.name,
      filesList.length > 1 ? filesList : undefined,
    );
  };

  const wrapperClass = embedded
    ? "mt-4"
    : "mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200";

  return (
    <>
    {saveMessage && (
      <div
        className={`mb-3 rounded-lg border px-4 py-2 text-sm ${
          saveMessage.type === "success"
            ? "border-green-200 bg-green-50 text-green-800"
            : "border-red-200 bg-red-50 text-red-800"
        }`}
      >
        {saveMessage.text}
      </div>
    )}
    <div className={wrapperClass}>
  <label className="block text-base font-bold text-gray-700 mb-3 flex items-center gap-2 relative">
    <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
      <IdCard className="w-4 h-4 text-violet-600" />
    </span>
    Identity Verification <span className="text-red-500">*</span>
    <div className="group">
      <Info className="w-5 h-5 text-green-600 cursor-help" />
      <div className="absolute left-0 top-full mt-2 hidden group-hover:block w-full sm:w-96 max-w-[calc(100vw-3rem)] p-4 bg-amber-50 border-2 border-amber-200 rounded-lg shadow-xl z-10">
        <p className="text-xs text-gray-500 mb-3">
          Please preview uploaded files. Unreadable files will delay
          your application.
        </p>
        <p className="text-xs text-gray-700 mt-2">
          We cannot accept links/shared files, please download files to your computer or phone directly and upload to our site.
        </p>
      </div>
    </div>
  </label>
  <p className="text-xs text-gray-500 mb-3">
    Please preview uploaded files. Unreadable files will delay your
    application.
  </p>
  <div className="flex gap-4 mb-4">
    <label
      className={`flex items-center ${isReadOnly ? "cursor-not-allowed" : ""}`}
    >
      <input
        data-testid={`${testIdPrefix}-drivers-license-radio`}
        type="radio"
        name={`${testIdPrefix}-governmentId`}
        value="drivers-license"
        checked={governmentIdType === "drivers-license"}
        onChange={() => {
          if (!isReadOnly) {
            chooseGovernmentIdType("drivers-license");
            setPassportImage(null);
            if (fieldErrors.governmentIdType) {
              setFieldErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.governmentIdType;
                return newErrors;
              });
            }
          }
        }}
        disabled={isReadOnly}
        className={`mr-2 ${isReadOnly ? "cursor-not-allowed opacity-50" : ""}`}
      />
      <span className="text-sm text-gray-700">Drivers License</span>
    </label>
    <label
      className={`flex items-center ${isReadOnly ? "cursor-not-allowed" : ""}`}
    >
      <input
        data-testid={`${testIdPrefix}-passport-radio`}
        type="radio"
        name={`${testIdPrefix}-governmentId`}
        value="passport"
        checked={governmentIdType === "passport"}
        onChange={() => {
          if (!isReadOnly) {
            chooseGovernmentIdType("passport");
            setDriversLicenseFront(null);
            setDriversLicenseBack(null);
            if (fieldErrors.governmentIdType) {
              setFieldErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.governmentIdType;
                return newErrors;
              });
            }
          }
        }}
        disabled={isReadOnly}
        className={`mr-2 ${isReadOnly ? "cursor-not-allowed opacity-50" : ""}`}
      />
      <span className="text-sm text-gray-700">Passport</span>
    </label>
  </div>
  {validateForm && fieldErrors.governmentIdType && (
    <div className="mb-4 text-sm text-red-600">
      <AlertCircle className="inline-block w-4 h-4 mr-2" />
      {fieldErrors.governmentIdType}
    </div>
  )}
  {validateForm && fieldErrors.governmentIdFiles && (
    <div className="mb-4 text-sm text-red-600">
      <AlertCircle className="inline-block w-4 h-4 mr-2" />
      {fieldErrors.governmentIdFiles}
    </div>
  )}

  {/* Driver's License Upload Fields */}
  {governmentIdType === "drivers-license" && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="p-4 border border-gray-200 rounded-lg bg-white">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload image or PDF of Driver's License (Front){" "}
          <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-500 mb-2">Image or PDF, max 5 MB</p>
        <div className="flex items-center gap-3">
          <input
            ref={driversLicenseFrontRef}
            type="file"
            accept={GOV_ID_FILE_ACCEPT}
            onChange={(e) => {
              if (!isReadOnly) {
                const file = e.target.files?.[0] || null;
                handleFileUpload(file, "front");
              }
            }}
            disabled={isReadOnly}
            className="hidden"
            id="drivers-license-front"
          />
          <label
            htmlFor="drivers-license-front"
            className={`px-4 py-2 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors inline-flex items-center gap-2 ${isReadOnly
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer"
              }`}
            onClick={(e) => {
              if (isReadOnly) {
                e.preventDefault();
              }
            }}
          >
            <Upload className="w-4 h-4" />
            {uploadingFront ? "Uploading..." : "Upload"}
            {uploadingFront && (
              <Loader2 className="w-4 h-4 animate-spin ml-1" />
            )}
          </label>
        </div>
        {/* Show file for newly uploaded file */}
        {driversLicenseFrontUrl && driversLicenseFront && (
          <div className="mt-3">
            {(() => {
              const fileName = driversLicenseFront.name;
              const isImage = isImageFile(fileName);

              // Collect all files (newly uploaded + existing) for filesList
              const allFiles: string[] = [];

              // Add newly uploaded files (blob URLs)
              if (driversLicenseFrontUrl)
                allFiles.push(driversLicenseFrontUrl);
              if (driversLicenseBackUrl)
                allFiles.push(driversLicenseBackUrl);
              if (passportImageUrl) allFiles.push(passportImageUrl);

              // Add existing files from API
              const governmentIdFront =
                applicationData?.government_id || [];
              const governmentIdBack =
                applicationData?.government_id_back || [];
              const allGovernmentIdFiles = [
                ...(Array.isArray(governmentIdFront)
                  ? governmentIdFront
                  : []),
                ...(Array.isArray(governmentIdBack)
                  ? governmentIdBack
                  : []),
              ];
              const allFilesWithIds = allGovernmentIdFiles.filter(
                (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                  f.attachment_id != null,
              );
              const applicantId = applicationData?.applicant_id;
              if (applicantId) {
                allFilesWithIds.forEach((f) => {
                  const fileTypeForUrl = governmentIdFront.includes(f)
                    ? "ID_PROOF"
                    : "ID_PROOF_BACK";
                  allFiles.push(
                    generateFilePreviewUrl(
                      f.attachment_id,
                      fileTypeForUrl,
                      applicantId,
                      "APPLICATION",
                    ),
                  );
                });
              }

              const filesList =
                allFiles.length > 1 ? allFiles : undefined;

              const openPreviewWithFreshToken = async () => {
                await ensureFileToken();
                const freshAllFiles: string[] = [];
                if (driversLicenseFrontUrl)
                  freshAllFiles.push(driversLicenseFrontUrl);
                if (driversLicenseBackUrl)
                  freshAllFiles.push(driversLicenseBackUrl);
                if (passportImageUrl)
                  freshAllFiles.push(passportImageUrl);
                const govFront = applicationData?.government_id || [];
                const govBack =
                  applicationData?.government_id_back || [];
                const allGovFiles = [
                  ...(Array.isArray(govFront) ? govFront : []),
                  ...(Array.isArray(govBack) ? govBack : []),
                ];
                const withIds = allGovFiles.filter(
                  (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                  f.attachment_id != null,
                );
                const applicantId = applicationData?.applicant_id;
                if (applicantId) {
                  withIds.forEach((f) => {
                    const ft = govFront.includes(f)
                      ? "ID_PROOF"
                      : "ID_PROOF_BACK";
                    freshAllFiles.push(
                      generateFilePreviewUrl(
                        f.attachment_id,
                        ft,
                        applicantId,
                        "APPLICATION",
                      ),
                    );
                  });
                }
                const list =
                  freshAllFiles.length > 1
                    ? freshAllFiles
                    : undefined;
                openFilePreviewByPath(
                  driversLicenseFrontUrl || "",
                  fileName || "driver-license-front.pdf",
                  list,
                );
              };

              const isPdf = isDocumentFile(fileName);

              return (
                <div className="mb-2">
                  <div
                    onClick={() => void openPreviewWithFreshToken()}
                    className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                  >
                    {isPdf && (
                      <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden />
                    )}
                    <span className="text-sm text-gray-700 flex-1 truncate">
                      {fileName}
                    </span>
                  </div>
                  {isImage && (
                    <div className="mt-2">
                      <Thumbnail
                        filepath={driversLicenseFrontUrl || ""}
                        fileName={fileName}
                        filesList={filesList}
                      />
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
        {/* Show files for existing files from API */}
        {!driversLicenseFront &&
          applicationData &&
          (() => {
            if (savedIsPassport) return null;

            const govIdAttachment =
              applicationData.goverment_id_attachment;
            if (!govIdAttachment) return null;

            const attachments =
              normalizeGovIdAttachments(govIdAttachment);

            if (attachments.length === 0) return null;

            return (
              <div className="mt-3 space-y-2">
                {attachments.map((file: ApplicationFileAttachment, index: number) => {
                  const fileName =
                    file.filename ||
                    file.name ||
                    "driver-license-front";
                  const isImage =
                    file.filepath && isImageFile(fileName);

                  return (
                    <div
                      key={file.attachment_id || index}
                      className="mb-2"
                    >
                      <div
                        onClick={() =>
                          handleFilePreview(
                            {
                              filepath: file.filepath,
                              name: fileName,
                              attachment_id: file.attachment_id,
                            },
                            "ID_PROOF",
                          )
                        }
                        className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                      >
                        <span className="text-sm text-gray-700 flex-1 truncate">
                          {fileName}
                        </span>
                      </div>
                      {isImage &&
                        (() => {
                          // Collect all files for filesList
                          const governmentIdFront =
                            applicationData?.government_id || [];
                          const governmentIdBack =
                            applicationData?.government_id_back || [];
                          const allGovernmentIdFiles = [
                            ...(Array.isArray(governmentIdFront)
                              ? governmentIdFront
                              : []),
                            ...(Array.isArray(governmentIdBack)
                              ? governmentIdBack
                              : []),
                          ];
                          const allFilesWithIds =
                            allGovernmentIdFiles.filter(
                              (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                  f.attachment_id != null,
                            );
                          const filesList =
                            allFilesWithIds.length > 1
                              ? allFilesWithIds.map((f) => {
                                const fileTypeForUrl =
                                  governmentIdFront.includes(f)
                                    ? "ID_PROOF"
                                    : "ID_PROOF_BACK";
                                return generateFilePreviewUrl(
                                  f.attachment_id,
                                  fileTypeForUrl,
                                  applicationData?.applicant_id ||
                                  0,
                                  "APPLICATION",
                                );
                              })
                              : undefined;

                          return (
                            <div className="mt-2">
                              <Thumbnail
                                filepath={file.filepath || ""}
                                attachmentId={file.attachment_id}
                                fileType="ID_PROOF"
                                fileName={fileName}
                                refId={applicationData?.applicant_id}
                                module="APPLICATION"
                                filesList={filesList}
                              />
                            </div>
                          );
                        })()}
                    </div>
                  );
                })}
              </div>
            );
          })()}
      </div>
      <div className="p-4 border border-gray-200 rounded-lg bg-white">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload image or PDF of Driver's License (Back){" "}
          <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-500 mb-2">Image or PDF, max 5 MB</p>
        <div className="flex items-center gap-3">
          <input
            ref={driversLicenseBackRef}
            type="file"
            accept={GOV_ID_FILE_ACCEPT}
            onChange={(e) => {
              if (!isReadOnly) {
                const file = e.target.files?.[0] || null;
                handleFileUpload(file, "back");
              }
            }}
            disabled={isReadOnly}
            className="hidden"
            id="drivers-license-back"
          />
          <label
            htmlFor="drivers-license-back"
            className={`px-4 py-2 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors inline-flex items-center gap-2 ${isReadOnly
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer"
              }`}
            onClick={(e) => {
              if (isReadOnly) {
                e.preventDefault();
              }
            }}
          >
            <Upload className="w-4 h-4" />
            {uploadingBack ? "Uploading..." : "Upload"}
            {uploadingBack && (
              <Loader2 className="w-4 h-4 animate-spin ml-1" />
            )}
          </label>
        </div>
        {/* Show file for newly uploaded file */}
        {driversLicenseBackUrl && driversLicenseBack && (
          <div className="mt-3">
            {(() => {
              const fileName = driversLicenseBack.name;
              const isImage = isImageFile(fileName);

              // Collect all files (newly uploaded + existing) for filesList
              const allFiles: string[] = [];

              // Add newly uploaded files (blob URLs)
              if (driversLicenseFrontUrl)
                allFiles.push(driversLicenseFrontUrl);
              if (driversLicenseBackUrl)
                allFiles.push(driversLicenseBackUrl);
              if (passportImageUrl) allFiles.push(passportImageUrl);

              // Add existing files from API
              const governmentIdFront =
                applicationData?.government_id || [];
              const governmentIdBack =
                applicationData?.government_id_back || [];
              const allGovernmentIdFiles = [
                ...(Array.isArray(governmentIdFront)
                  ? governmentIdFront
                  : []),
                ...(Array.isArray(governmentIdBack)
                  ? governmentIdBack
                  : []),
              ];
              const allFilesWithIds = allGovernmentIdFiles.filter(
                (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                  f.attachment_id != null,
              );
              const applicantId = applicationData?.applicant_id;
              if (applicantId) {
                allFilesWithIds.forEach((f) => {
                  const fileTypeForUrl = governmentIdFront.includes(f)
                    ? "ID_PROOF"
                    : "ID_PROOF_BACK";
                  allFiles.push(
                    generateFilePreviewUrl(
                      f.attachment_id,
                      fileTypeForUrl,
                      applicantId,
                      "APPLICATION",
                    ),
                  );
                });
              }

              const filesList =
                allFiles.length > 1 ? allFiles : undefined;

              const openBackPreviewWithFreshToken = async () => {
                await ensureFileToken();
                const freshAllFiles: string[] = [];
                if (driversLicenseFrontUrl)
                  freshAllFiles.push(driversLicenseFrontUrl);
                if (driversLicenseBackUrl)
                  freshAllFiles.push(driversLicenseBackUrl);
                if (passportImageUrl)
                  freshAllFiles.push(passportImageUrl);
                const govFront = applicationData?.government_id || [];
                const govBack =
                  applicationData?.government_id_back || [];
                const allGovFiles = [
                  ...(Array.isArray(govFront) ? govFront : []),
                  ...(Array.isArray(govBack) ? govBack : []),
                ];
                const withIds = allGovFiles.filter(
                  (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                  f.attachment_id != null,
                );
                const applicantId = applicationData?.applicant_id;
                if (applicantId) {
                  withIds.forEach((f) => {
                    const ft = govFront.includes(f)
                      ? "ID_PROOF"
                      : "ID_PROOF_BACK";
                    freshAllFiles.push(
                      generateFilePreviewUrl(
                        f.attachment_id,
                        ft,
                        applicantId,
                        "APPLICATION",
                      ),
                    );
                  });
                }
                const list =
                  freshAllFiles.length > 1
                    ? freshAllFiles
                    : undefined;
                openFilePreviewByPath(
                  driversLicenseBackUrl || "",
                  fileName || "driver-license-back.pdf",
                  list,
                );
              };

              const isPdf = isDocumentFile(fileName);

              return (
                <div className="mb-2">
                  <div
                    onClick={() =>
                      void openBackPreviewWithFreshToken()
                    }
                    className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                  >
                    {isPdf && (
                      <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden />
                    )}
                    <span className="text-sm text-gray-700 flex-1 truncate">
                      {fileName}
                    </span>
                  </div>
                  {isImage && (
                    <div className="mt-2">
                      <Thumbnail
                        filepath={driversLicenseBackUrl || ""}
                        fileName={fileName}
                        filesList={filesList}
                      />
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
        {/* Show files for existing files from API */}
        {!driversLicenseBack &&
          applicationData &&
          (() => {
            const govIdAttachmentBack =
              applicationData.goverment_id_attachment_back;
            if (!govIdAttachmentBack) return null;

            const backAttachments =
              normalizeGovIdAttachments(govIdAttachmentBack);

            if (backAttachments.length === 0) return null;

            return (
              <div className="mt-3 space-y-2">
                {backAttachments.map((file: ApplicationFileAttachment, index: number) => {
                  const fileName =
                    file.filename ||
                    file.name ||
                    "driver-license-back";
                  const isImage =
                    file.filepath && isImageFile(fileName);

                  return (
                    <div
                      key={file.attachment_id || index}
                      className="mb-2"
                    >
                      <div
                        onClick={() =>
                          handleFilePreview(
                            {
                              filepath: file.filepath,
                              name: fileName,
                              attachment_id: file.attachment_id,
                            },
                            "ID_PROOF_BACK",
                          )
                        }
                        className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                      >
                        <span className="text-sm text-gray-700 flex-1 truncate">
                          {fileName}
                        </span>
                      </div>
                      {isImage &&
                        (() => {
                          // Collect all files for filesList
                          const governmentIdFront =
                            applicationData?.government_id || [];
                          const governmentIdBack =
                            applicationData?.government_id_back || [];
                          const allGovernmentIdFiles = [
                            ...(Array.isArray(governmentIdFront)
                              ? governmentIdFront
                              : []),
                            ...(Array.isArray(governmentIdBack)
                              ? governmentIdBack
                              : []),
                          ];
                          const allFilesWithIds =
                            allGovernmentIdFiles.filter(
                              (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                  f.attachment_id != null,
                            );
                          const filesList =
                            allFilesWithIds.length > 1
                              ? allFilesWithIds.map((f) => {
                                const fileTypeForUrl =
                                  governmentIdFront.includes(f)
                                    ? "ID_PROOF"
                                    : "ID_PROOF_BACK";
                                return generateFilePreviewUrl(
                                  f.attachment_id,
                                  fileTypeForUrl,
                                  applicationData?.applicant_id ||
                                  0,
                                  "APPLICATION",
                                );
                              })
                              : undefined;

                          return (
                            <div className="mt-2">
                              <Thumbnail
                                filepath={file.filepath || ""}
                                attachmentId={file.attachment_id}
                                fileType="ID_PROOF_BACK"
                                fileName={fileName}
                                refId={applicationData?.applicant_id}
                                module="APPLICATION"
                                filesList={filesList}
                              />
                            </div>
                          );
                        })()}
                    </div>
                  );
                })}
              </div>
            );
          })()}
      </div>
    </div>
  )}

  {/* Passport Upload Field */}
  {governmentIdType === "passport" && (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Upload image or PDF of Passport{" "}
        <span className="text-red-500">*</span>
      </label>
      <p className="text-xs text-gray-500 mb-2">Image or PDF, max 5 MB</p>
      {(!hasPassportFile || uploadingPassport) && (
      <div className="flex items-center gap-3">
        <input
          ref={passportRef}
          type="file"
          accept={GOV_ID_FILE_ACCEPT}
          onChange={(e) => {
            if (!isReadOnly) {
              const file = e.target.files?.[0] || null;
              handleFileUpload(file, "passport");
            }
          }}
          disabled={isReadOnly}
          className="hidden"
          id="passport"
        />
        <label
          htmlFor="passport"
          className={`px-4 py-2 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors inline-flex items-center gap-2 ${isReadOnly
            ? "opacity-50 cursor-not-allowed"
            : "cursor-pointer"
            }`}
          onClick={(e) => {
            if (isReadOnly) {
              e.preventDefault();
            }
          }}
        >
          <Upload className="w-4 h-4" />
          {uploadingPassport ? "Uploading..." : "Upload"}
          {uploadingPassport && (
            <Loader2 className="w-4 h-4 animate-spin ml-1" />
          )}
        </label>
      </div>
      )}
      {/* Show file for newly uploaded file */}
      {passportImageUrl && passportImage && (
        <div className="mt-3">
          {(() => {
            const fileName = passportImage.name;
            const isImage = isImageFile(fileName);

            // Collect all files (newly uploaded + existing) for filesList
            const allFiles: string[] = [];

            // Add newly uploaded files (blob URLs)
            if (driversLicenseFrontUrl)
              allFiles.push(driversLicenseFrontUrl);
            if (driversLicenseBackUrl)
              allFiles.push(driversLicenseBackUrl);
            if (passportImageUrl) allFiles.push(passportImageUrl);

            // Add existing files from API
            const governmentIdFront =
              applicationData?.government_id || [];
            const governmentIdBack =
              applicationData?.government_id_back || [];
            const allGovernmentIdFiles = [
              ...(Array.isArray(governmentIdFront)
                ? governmentIdFront
                : []),
              ...(Array.isArray(governmentIdBack)
                ? governmentIdBack
                : []),
            ];
            const allFilesWithIds = allGovernmentIdFiles.filter(
                  (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                    f.attachment_id != null,
            );
            const applicantId = applicationData?.applicant_id;
            if (applicantId) {
              allFilesWithIds.forEach((f) => {
                const fileTypeForUrl = governmentIdFront.includes(f)
                  ? "ID_PROOF"
                  : "ID_PROOF_BACK";
                allFiles.push(
                  generateFilePreviewUrl(
                    f.attachment_id,
                    fileTypeForUrl,
                    applicantId,
                    "APPLICATION",
                  ),
                );
              });
            }

            const filesList =
              allFiles.length > 1 ? allFiles : undefined;

            const openPassportPreviewWithFreshToken = async () => {
              await ensureFileToken();
              const freshAllFiles: string[] = [];
              if (driversLicenseFrontUrl)
                freshAllFiles.push(driversLicenseFrontUrl);
              if (driversLicenseBackUrl)
                freshAllFiles.push(driversLicenseBackUrl);
              if (passportImageUrl)
                freshAllFiles.push(passportImageUrl);
              const govFront = applicationData?.government_id || [];
              const govBack =
                applicationData?.government_id_back || [];
              const allGovFiles = [
                ...(Array.isArray(govFront) ? govFront : []),
                ...(Array.isArray(govBack) ? govBack : []),
              ];
              const withIds = allGovFiles.filter(
                (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                  f.attachment_id != null,
              );
              const applicantId = applicationData?.applicant_id;
              if (applicantId) {
                withIds.forEach((f) => {
                  const ft = govFront.includes(f)
                    ? "ID_PROOF"
                    : "ID_PROOF_BACK";
                  freshAllFiles.push(
                    generateFilePreviewUrl(
                      f.attachment_id,
                      ft,
                      applicantId,
                      "APPLICATION",
                    ),
                  );
                });
              }
              const list =
                freshAllFiles.length > 1 ? freshAllFiles : undefined;
              openFilePreviewByPath(
                passportImageUrl || "",
                fileName || "passport.pdf",
                list,
              );
            };

            const isPdf = isDocumentFile(fileName);

            return (
              <div className="mb-2">
                <div
                  onClick={() =>
                    void openPassportPreviewWithFreshToken()
                  }
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                >
                  {isPdf && (
                    <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden />
                  )}
                  <span className="text-sm text-gray-700 flex-1 truncate">
                    {fileName}
                  </span>
                </div>
                {isImage && (
                  <div className="mt-2">
                    <Thumbnail
                      filepath={passportImageUrl || ""}
                      fileName={fileName}
                      filesList={filesList}
                    />
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
      {/* Show files for existing files from API */}
      {!passportImage &&
        applicationData &&
        (() => {
          if (savedIsDriversLicense) return null;

          const govIdAttachment =
            applicationData.goverment_id_attachment;
          if (!govIdAttachment) return null;

          const attachments =
            normalizeGovIdAttachments(govIdAttachment);

          if (attachments.length === 0) return null;

          return (
            <div className="mt-3 space-y-2">
              {attachments.map((file: ApplicationFileAttachment, index: number) => {
                const fileName =
                  file.filename || file.name || "passport";
                const isImage =
                  file.filepath && isImageFile(fileName);

                return (
                  <div
                    key={file.attachment_id || index}
                    className="mb-2"
                  >
                    <div
                      onClick={() =>
                        handleFilePreview(
                          {
                            filepath: file.filepath,
                            name: fileName,
                            attachment_id: file.attachment_id,
                          },
                          "ID_PROOF",
                        )
                      }
                      className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                    >
                      <span className="text-sm text-gray-700 flex-1 truncate">
                        {fileName}
                      </span>
                    </div>
                    {isImage &&
                      (() => {
                        // Collect all files for filesList (passport uses ID_PROOF type)
                        const governmentIdFront =
                          applicationData.government_id || [];
                        const governmentIdBack =
                          applicationData.government_id_back || [];
                        const allGovernmentIdFiles = [
                          ...(Array.isArray(governmentIdFront)
                            ? governmentIdFront
                            : []),
                          ...(Array.isArray(governmentIdBack)
                            ? governmentIdBack
                            : []),
                        ];
                        const allFilesWithIds =
                          allGovernmentIdFiles.filter(
                            (f): f is ApplicationFileAttachment & { attachment_id: number } =>
                  f.attachment_id != null,
                          );
                        const filesList =
                          allFilesWithIds.length > 1
                            ? allFilesWithIds.map((f) => {
                              const fileTypeForUrl =
                                governmentIdFront.includes(f)
                                  ? "ID_PROOF"
                                  : "ID_PROOF_BACK";
                              return generateFilePreviewUrl(
                                f.attachment_id,
                                fileTypeForUrl,
                                applicationData?.applicant_id || 0,
                                "APPLICATION",
                              );
                            })
                            : undefined;

                        return (
                          <div className="mt-2">
                            <Thumbnail
                              filepath={file.filepath || ""}
                              attachmentId={file.attachment_id}
                              fileType="ID_PROOF"
                              fileName={fileName}
                              refId={applicationData?.applicant_id}
                              module="APPLICATION"
                              filesList={filesList}
                            />
                          </div>
                        );
                      })()}
                  </div>
                );
              })}
            </div>
          );
        })()}
    </div>
  )}
    </div>
    </>
  );
}

